import { Text, Input, Button, Flex, Form, Label, Grid } from '@pega/cosmos-react-core';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useLocalTranslation from '../../../hooks/useLocalTranslation';
import { appState } from '../../../interfaces/common';
import { updateBannerInfo, updateLoaderDetails } from '../../../redux/actions';
import SectionRenderTemplate from '../../Utilities/SectionRenderTemplate';
import CreateVonage from '../Vonage/CreateVonage';
import { addNewTwilioWhatsappAccount } from './TwilioWhatsappApis';
import DisplayVonageTwilioOptions from '../../Utilities/DisplayVonageTwilioOptions';

interface TwilioWhatsappFormData {
  accountSid: string;
  authToken: string;
  name: string;
  phoneNumber: string;
}

const CreateTwilioWhatsapp: React.FC = () => {
  const { register, handleSubmit, errors, reset, formState } = useForm<TwilioWhatsappFormData>({ mode: 'all' });
  const sessionId = useSelector((state: appState) => state.sessionId);
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const navigate = useNavigate();
  const [createWhatsappType, setcreateWhatsappType] = useState('twilio');

  const onSubmit = async (data: TwilioWhatsappFormData) => {
    const payload = {
      account_sid: data.accountSid,
      auth_token: data.authToken,
      name: data.name,
      phone_number: data.phoneNumber,
    };
    if (formState.touched) {
      dispatch(updateLoaderDetails(true));
      const json = await addNewTwilioWhatsappAccount(sessionId, payload);
      dispatch(updateLoaderDetails(false));
      if (json?.status === 200) {
        reset();
        navigate(`/twilio-whatsapp/${json?.data?.connectionId}`);
        dispatch(updateBannerInfo(`Twilio Whatsapp ${translate('account_registered')}`, 'success'));
      } else if (json?.status === 403) {
        navigate('/');
        dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
      } else {
        let errorMessage;
        if (json?.data?.code) {
          errorMessage = translate(json.data.code);
        }
        dispatch(updateBannerInfo(errorMessage || translate('whatsapp_creation_failure'), 'urgent'));
      }
    }
  };

  const actions = (
    <Flex>
      <Button name="Save" type="submit" variant="primary">
        {translate('save')}
      </Button>
    </Flex>
  );

  const headerElement = () => {
    return (
      <Text variant="h1" as="h1">
        {`${translate('new')} ${translate('WhatsApp')}`}
      </Text>
    );
  };

  return (
    <SectionRenderTemplate header={headerElement()}>
      {process.env.REACT_APP_ENABLE_VONAGE === '1' && (
        <DisplayVonageTwilioOptions
          createAccountVia={createWhatsappType}
          setAccountType={setcreateWhatsappType}
          accountType="create_whatsapp_type"
        />
      )}
      <Grid
        container={{
          cols: 'repeat(2, 1fr)',
          gap: 2,
        }}
      >
        {createWhatsappType === 'twilio' ? (
          <Form actions={actions} className="basic-form" onSubmit={handleSubmit(onSubmit)}>
            <Input
              name="name"
              label={translate('name')}
              required={true}
              autoComplete="off"
              info={errors.name?.message}
              status={errors.name?.message ? 'error' : undefined}
              ref={register({ required: translate('name_empty') })}
            />

            <Input
              name="phoneNumber"
              type="text"
              label={translate('number')}
              autoComplete="off"
              placeholder="+1 555 555 5555"
              info={errors.phoneNumber?.message}
              required={true}
              status={errors.phoneNumber?.message ? 'error' : undefined}
              ref={register({
                required: translate('phone_number_criteria_error'),
                pattern: { value: /^[+]{1}[\s0-9]*$/, message: translate('phone_number_criteria_error_1') },
              })}
            />
            <Input
              name="accountSid"
              label={translate('account_sid')}
              autoComplete="off"
              type="text"
              info={errors.accountSid?.message}
              required={true}
              status={errors.accountSid?.message ? 'error' : undefined}
              ref={register({
                required: translate('account_sid_criteria_error'),
                pattern: { value: /^\w*$/, message: translate('account_sid_criteria_error_1') },
              })}
            />
            <Input
              name="authToken"
              label={translate('auth_token')}
              autoComplete="off"
              info={errors.authToken?.message}
              required={true}
              status={errors.authToken?.message ? 'error' : undefined}
              ref={register({ required: translate('auth_token_empty') })}
            />
            <Flex
              container={{
                direction: 'column',
                itemGap: 1,
              }}
            >
              <Label>{translate('webhook')}</Label>
              <Text>{process.env.REACT_APP_INCOMING_WEBHOOK}/twilio-whatsapp</Text>
            </Flex>
          </Form>
        ) : (
          <CreateVonage type="whatsapp" />
        )}
      </Grid>
    </SectionRenderTemplate>
  );
};

export default CreateTwilioWhatsapp;
